import React, { useRef, useEffect, useState } from 'react'
import SignaturePad from 'signature_pad'

interface SignaturePadComponentProps {
  onSave: (dataUrl: string) => void
  onCancel?: () => void
  height?: number
}

export const SignaturePadComponent: React.FC<SignaturePadComponentProps> = ({
  onSave,
  onCancel,
  height = 200,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const signaturePadRef = useRef<SignaturePad | null>(null)
  const [isEmpty, setIsEmpty] = useState(true)

  useEffect(() => {
    if (canvasRef.current) {
      signaturePadRef.current = new SignaturePad(canvasRef.current, {
        backgroundColor: 'rgb(250, 250, 250)',
        penColor: 'rgb(51, 51, 51)',
        minWidth: 1,
        maxWidth: 3,
      })

      // 监听笔画结束事件
      signaturePadRef.current.addEventListener('endStroke', () => {
        setIsEmpty(signaturePadRef.current?.isEmpty() ?? true)
      })

      // 响应窗口大小变化
      const resizeCanvas = () => {
        if (canvasRef.current && signaturePadRef.current) {
          const ratio = Math.max(window.devicePixelRatio || 1, 1)
          canvasRef.current.width = canvasRef.current.offsetWidth * ratio
          canvasRef.current.height = canvasRef.current.offsetHeight * ratio
          canvasRef.current.getContext('2d')?.scale(ratio, ratio)
          signaturePadRef.current.clear()
        }
      }

      resizeCanvas()
      window.addEventListener('resize', resizeCanvas)

      return () => {
        window.removeEventListener('resize', resizeCanvas)
        signaturePadRef.current?.off()
      }
    }
  }, [height])

  const handleClear = () => {
    signaturePadRef.current?.clear()
    setIsEmpty(true)
  }

  const handleSave = () => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      const dataUrl = signaturePadRef.current.toDataURL('image/png')
      onSave(dataUrl)
    }
  }

  return (
    <div className="bg-white rounded-lg">
      {/* 签名区域 */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-gray-50">
        <canvas
          ref={canvasRef}
          style={{ width: '100%', height: `${height}px` }}
        />
      </div>

      {/* 提示文字 */}
      {isEmpty && (
        <div className="text-center text-gray-400 text-sm mt-2">
          请在上方区域内签名
        </div>
      )}

      {/* 操作按钮 */}
      <div className="flex gap-3 mt-4">
        <button
          onClick={handleClear}
          className="flex-1 py-2.5 px-4 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
        >
          清除
        </button>
        {onCancel && (
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 px-4 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
          >
            取消
          </button>
        )}
        <button
          onClick={handleSave}
          disabled={isEmpty}
          className={`flex-1 py-2.5 px-4 rounded-lg text-white transition-colors ${
            isEmpty
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-red-600 hover:bg-red-700'
          }`}
        >
          确认签名
        </button>
      </div>
    </div>
  )
}
